import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { ChevronDown, ChevronUp, ArrowRight, BookOpen, CheckCircle, FileText, ZoomIn, X } from 'lucide-react';
import { usePreferences } from '../context/PreferencesContext';
import { toast } from 'sonner';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { VideoModal, WatchVideoButton } from '../components/common/VideoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WrittenPracticeSessionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preferences } = usePreferences();
  const config = location.state?.config;

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [marksGiven, setMarksGiven] = useState('');
  const [questionScores, setQuestionScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState('');
  const [videoModal, setVideoModal] = useState({ open: false, url: null });

  useEffect(() => {
    if (!config) {
      toast.error('Invalid session configuration');
      navigate('/practice');
      return;
    }

    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      params.append('function_id', config.function_id);
      params.append('num_questions', config.num_questions);
      
      if (config.selected_topics && config.selected_topics.length > 0) {
        params.append('topics', config.selected_topics.join(','));
      }
      
      if (config.date_from_year && config.date_from_year !== 'all') {
        params.append('date_from_year', config.date_from_year);
        if (config.date_from_month && config.date_from_month !== 'all') {
          params.append('date_from_month', config.date_from_month);
        }
      }
      
      if (config.date_to_year && config.date_to_year !== 'all') {
        params.append('date_to_year', config.date_to_year);
        if (config.date_to_month && config.date_to_month !== 'all') {
          params.append('date_to_month', config.date_to_month);
        }
      }
      
      if (config.asked_filter && config.asked_filter !== 'all') {
        params.append('asked_filter', config.asked_filter);
      }

      const response = await axios.post(`${API}/practice/written/questions?${params.toString()}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
      } else {
        toast.error('No questions found with the selected filters');
        navigate('/practice');
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!showAnswer) {
      toast.error('Please reveal the answer first to assess yourself');
      return;
    }

    const marks = parseInt(marksGiven) || 0;
    const maxMarks = questions[currentIndex].marks || 10;

    if (marks < 0 || marks > maxMarks) {
      toast.error(`Marks must be between 0 and ${maxMarks}`);
      return;
    }

    // Save score for this question
    const newScores = [...questionScores, {
      question_id: questions[currentIndex].id,
      marks_given: marks,
      max_marks: maxMarks
    }];
    setQuestionScores(newScores);

    // Move to next question or finish
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setMarksGiven('');
      setShowAnswer(false);
    } else {
      // Session complete - save and show results
      finishSession(newScores);
    }
  };

  const finishSession = async (scores) => {
    try {
      const totalObtained = scores.reduce((sum, s) => sum + s.marks_given, 0);
      const totalPossible = scores.reduce((sum, s) => sum + s.max_marks, 0);
      const percentage = totalPossible > 0 ? (totalObtained / totalPossible) * 100 : 0;
      const passed = percentage >= 60;

      // Build date range strings
      let dateRangeFrom = null;
      let dateRangeTo = null;
      
      if (config.date_from_year && config.date_from_year !== 'all') {
        dateRangeFrom = config.date_from_month && config.date_from_month !== 'all'
          ? `${config.date_from_month} ${config.date_from_year}`
          : config.date_from_year;
      }
      
      if (config.date_to_year && config.date_to_year !== 'all') {
        dateRangeTo = config.date_to_month && config.date_to_month !== 'all'
          ? `${config.date_to_month} ${config.date_to_year}`
          : config.date_to_year;
      }

      const sessionData = {
        function_id: config.function_id,
        topics: config.selected_topics || [],
        num_questions: questions.length,
        total_marks_obtained: totalObtained,
        total_marks_possible: totalPossible,
        percentage: parseFloat(percentage.toFixed(2)),
        passed: passed,
        question_scores: scores,
        date_range_from: dateRangeFrom,
        date_range_to: dateRangeTo,
        asked_filter: config.asked_filter !== 'all' ? config.asked_filter : null
      };

      await axios.post(`${API}/practice/written/save`, sessionData);

      // Navigate to results
      navigate('/practice/written/results', { 
        state: { 
          scores,
          totalObtained,
          totalPossible,
          percentage: parseFloat(percentage.toFixed(2)),
          passed,
          config,
          questions
        } 
      });
    } catch (error) {
      console.error('Failed to save session:', error);
      toast.error('Failed to save session, but showing results');
      
      // Still show results even if save failed
      const totalObtained = scores.reduce((sum, s) => sum + s.marks_given, 0);
      const totalPossible = scores.reduce((sum, s) => sum + s.max_marks, 0);
      const percentage = totalPossible > 0 ? (totalObtained / totalPossible) * 100 : 0;
      
      navigate('/practice/written/results', { 
        state: { 
          scores,
          totalObtained,
          totalPossible,
          percentage: parseFloat(percentage.toFixed(2)),
          passed: percentage >= 60,
          config,
          questions
        } 
      });
    }
  };

  const openLightbox = (url, caption) => {
    setLightboxImage(url);
    setLightboxCaption(caption || '');
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxCaption('');
  };

  // Render image with zoom overlay
  const renderImageWithZoom = (src, alt, caption) => (
    <div 
      key={src}
      className="relative group cursor-pointer inline-block"
      onClick={() => openLightbox(src, caption)}
    >
      <img 
        src={src} 
        alt={alt}
        className="max-w-full h-auto rounded-lg border border-gray-200 max-h-48 object-contain"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center">
        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner message="Loading questions..." />;
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12">
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Questions Available</h3>
              <p className="text-gray-500 mb-6">
                No questions found matching your configuration
              </p>
              <Button onClick={() => navigate('/practice')}>
                Back to Configuration
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const maxMarks = currentQuestion.marks || 10;

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Question {currentIndex + 1} of {questions.length}
            </h2>
            <Badge className="bg-[#0A7EA4]">
              <FileText className="w-3 h-3 mr-1" />
              Written Practice
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardContent className="p-8">
            {/* Question Text */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {currentQuestion.question_text}
              </h3>
              
              {/* Question Images */}
              {currentQuestion.question_images && currentQuestion.question_images.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {currentQuestion.question_images.map((img, idx) => 
                    renderImageWithZoom(
                      typeof img === 'string' ? img : img.url,
                      `Question image ${idx + 1}`,
                      typeof img === 'object' ? img.caption : null
                    )
                  )}
                </div>
              )}
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-[#0A7EA4]">{preferences.grade}</Badge>
              <Badge className="bg-purple-600">{maxMarks} Marks</Badge>
              {currentQuestion.topic_name && (
                <Badge variant="outline" className="max-w-full whitespace-normal break-words text-left">{currentQuestion.topic_name}</Badge>
              )}
              {currentQuestion.asked_count > 1 && (
                <Badge className="bg-amber-500">Asked {currentQuestion.asked_count}x</Badge>
              )}
            </div>

            {/* View Answer Section */}
            <div className="mb-6">
              <Button
                onClick={() => setShowAnswer(!showAnswer)}
                variant="outline"
                className="w-full justify-between mb-3"
              >
                <span>View Model Answer</span>
                {showAnswer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>

              {showAnswer && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  {currentQuestion.is_answered ? (
                    <div className="space-y-3">
                      {currentQuestion.answer_text && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
                            Model Answer:
                            {currentQuestion.video_url && (
                              <WatchVideoButton
                                videoUrl={currentQuestion.video_url}
                                onClick={() => setVideoModal({ open: true, url: currentQuestion.video_url })}
                              />
                            )}
                          </h4>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{currentQuestion.answer_text}</p>
                        </div>
                      )}
                      {/* Answer Images */}
                      {currentQuestion.answer_images && currentQuestion.answer_images.length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-3">
                          {currentQuestion.answer_images.map((img, idx) => 
                            renderImageWithZoom(
                              typeof img === 'string' ? img : img.url,
                              `Answer image ${idx + 1}`,
                              typeof img === 'object' ? img.caption : null
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Answer Coming Soon</p>
                      <p className="text-xs text-gray-500 mt-1">Answer is under review</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Self-Assessment Score */}
            {showAnswer && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-sm font-medium text-blue-900 mb-2 block">
                  How many marks would you give yourself? (0-{maxMarks})
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="0"
                    max={maxMarks}
                    value={marksGiven}
                    onChange={(e) => setMarksGiven(e.target.value)}
                    placeholder={`Enter marks (max ${maxMarks})`}
                    className="w-32 text-lg"
                  />
                  <span className="text-gray-500">/ {maxMarks}</span>
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  Compare your answer with the model answer above and rate yourself honestly
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Next Button */}
        <Button
          onClick={handleNext}
          className="w-full bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 py-6 text-lg"
          disabled={!showAnswer}
        >
          {currentIndex < questions.length - 1 ? (
            <>
              Next Question
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          ) : (
            <>
              Finish Session
              <CheckCircle className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Image Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
          <DialogTitle className="sr-only">Image Preview</DialogTitle>
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 z-50 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            {lightboxCaption && (
              <p className="text-white text-center mt-4 px-4 text-sm">
                {lightboxCaption}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Explainer Video Modal — YouTube / Dadan iframe */}
      {videoModal.open && (
        <VideoModal
          videoUrl={videoModal.url}
          onClose={() => setVideoModal({ open: false, url: null })}
        />
      )}
    </Layout>
  );
};

export default WrittenPracticeSessionPage;
